import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Minus, Plus, X, ShoppingBag, ArrowRight, Tag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function Cart() {
  const { items, updateQuantity, removeItem, subtotal, discount, shipping, total, coupon, applyCoupon, removeCoupon } = useCart();
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [publicCoupons, setPublicCoupons] = useState([]);

  useEffect(() => {
    api.get("/coupons/public").then(({ data }) => setPublicCoupons(data)).catch(() => {});
  }, []);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setApplying(true);
    try {
      const data = await applyCoupon(code.trim());
      toast.success(`Coupon applied: -₹${data.discount.toFixed(2)}`);
      setCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid coupon");
    }
    setApplying(false);
  };

  const applyHint = async (hintCode) => {
    setApplying(true);
    try {
      const data = await applyCoupon(hintCode);
      toast.success(`Coupon applied: -₹${data.discount.toFixed(2)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply");
    }
    setApplying(false);
  };

  // Pick best hint the customer isn't already using; prefer ones they qualify for
  const hint = !coupon && publicCoupons.length > 0 && subtotal > 0
    ? (publicCoupons.find((c) => subtotal >= (c.min_order || 0)) || publicCoupons[0])
    : null;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <div className="w-16 h-16 mx-auto mb-6 bg-[#F3EFEA] rounded-full flex items-center justify-center">
          <ShoppingBag className="w-6 h-6 text-[#9BA595]" strokeWidth={1.5} />
        </div>
        <h2 className="font-serif-display text-5xl mb-4">Your cart is empty</h2>
        <p className="text-sm text-[#9BA595] mb-8">Bring home a candle that changes the whole room.</p>
        <Link
          to="/shop"
          className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#FDFBF7] px-7 py-3.5 rounded-full text-sm uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors"
          data-testid="cart-empty-shop-btn"
        >
          Browse the shop
          <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="cart-page">
      <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ Your cart</div>
      <h1 className="font-serif-display text-5xl md:text-6xl leading-tight mb-12">Almost yours</h1>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-6">
          {items.map((item) => (
            <div
              key={item.product_id}
              className="flex gap-4 md:gap-6 pb-6 border-b border-[#E3DCD2]"
              data-testid={`cart-item-${item.slug}`}
            >
              <Link to={`/product/${item.slug}`} className="w-24 md:w-32 aspect-[4/5] shrink-0 overflow-hidden rounded-md bg-[#F3EFEA]">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-4">
                  <Link to={`/product/${item.slug}`}>
                    <h3 className="font-serif-display text-xl md:text-2xl leading-tight hover:text-[#D27D60] transition-colors">{item.name}</h3>
                    <div className="text-sm text-[#9BA595] mt-1">₹{item.price} each</div>
                    {item.details && item.details.length > 0 && (
                      <ul className="text-xs text-[#4a4a4a] mt-2 space-y-0.5" data-testid={`cart-item-details-${item.slug}`}>
                        {item.details.map((d, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-[#D27D60]" /> {d}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Link>
                  <button
                    onClick={() => removeItem(item.product_id)}
                    className="p-2 hover:text-[#D27D60] transition-colors"
                    data-testid={`cart-remove-${item.slug}`}
                    aria-label="Remove item"
                  >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>

                <div className="mt-auto flex items-end justify-between pt-4">
                  <div className="flex items-center border border-[#E3DCD2] rounded-full">
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      className="p-2 hover:text-[#D27D60] transition-colors"
                      data-testid={`cart-qty-dec-${item.slug}`}
                    >
                      <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                    <span className="px-3 text-sm min-w-[2rem] text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      className="p-2 hover:text-[#D27D60] transition-colors"
                      data-testid={`cart-qty-inc-${item.slug}`}
                    >
                      <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="text-lg font-medium" data-testid={`cart-line-total-${item.slug}`}>
                    ₹{item.price * item.quantity}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-4">
          <div className="p-8 bg-[#F3EFEA]/60 rounded-lg sticky top-32">
            <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-6">Order summary</div>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">₹{subtotal.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm text-[#D27D60]" data-testid="cart-discount-line">
                  <span>Discount {coupon && `(${coupon.code})`}</span>
                  <span>-₹{discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span>Shipping</span>
                <span data-testid="cart-shipping">{shipping === 0 ? "Free" : `₹${shipping}`}</span>
              </div>
              {subtotal - discount < 699 && subtotal - discount > 0 && (
                <div className="text-xs text-[#9BA595] italic">Add ₹{(699 - (subtotal - discount)).toFixed(0)} more for free shipping</div>
              )}
            </div>

            {/* Coupon */}
            <div className="mb-6 pb-6 border-b border-[#E3DCD2]">
              {coupon ? (
                <div className="flex items-center justify-between p-3 bg-[#FDFBF7] rounded-md border border-[#D27D60]/40" data-testid="cart-coupon-applied">
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="w-3.5 h-3.5 text-[#D27D60]" strokeWidth={1.5} />
                    <span className="font-medium uppercase tracking-wider">{coupon.code}</span>
                    <span className="text-xs text-[#9BA595]">applied</span>
                  </div>
                  <button
                    onClick={() => { removeCoupon(); toast.success("Coupon removed"); }}
                    className="text-[#9BA595] hover:text-[#D27D60] transition-colors"
                    data-testid="cart-coupon-remove"
                    aria-label="Remove coupon"
                  >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <>
                  <form onSubmit={handleApply} className="flex gap-2" data-testid="cart-coupon-form">
                    <div className="relative flex-1">
                      <Tag className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9BA595]" strokeWidth={1.5} />
                      <input
                        type="text"
                        placeholder="Coupon code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="w-full bg-transparent border border-[#E3DCD2] rounded-full pl-9 pr-4 py-2.5 text-sm outline-none focus:border-[#D27D60] transition-colors uppercase tracking-wider"
                        data-testid="cart-coupon-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={applying}
                      className="px-4 py-2.5 rounded-full text-xs uppercase tracking-widest-plus border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-colors disabled:opacity-50"
                      data-testid="cart-coupon-apply"
                    >
                      {applying ? "..." : "Apply"}
                    </button>
                  </form>
                  {hint && (
                    <button
                      type="button"
                      onClick={() => applyHint(hint.code)}
                      disabled={applying || subtotal < (hint.min_order || 0)}
                      className="mt-3 w-full flex items-center gap-2 p-3 rounded-md bg-[#FDFBF7] border border-dashed border-[#D27D60]/50 text-left hover:border-[#D27D60] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="cart-coupon-hint"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-[#D27D60] shrink-0" strokeWidth={1.5} />
                      <div className="flex-1 text-xs">
                        <div className="uppercase tracking-widest-plus text-[#9BA595] mb-0.5">
                          {subtotal < (hint.min_order || 0)
                            ? `Add ₹${Math.ceil(hint.min_order - subtotal)} to unlock`
                            : "Tap to apply"}
                        </div>
                        <div className="text-[#1A1A1A] font-medium">
                          <span className="text-[#D27D60]">{hint.code}</span>
                          {" — "}
                          {hint.description || (
                            hint.discount_type === "percent"
                              ? `${hint.discount_value}% off`
                              : `₹${hint.discount_value} off`
                          )}
                          {hint.min_order > 0 && !(hint.description || "").toLowerCase().includes("min") && ` (min ₹${hint.min_order})`}
                        </div>
                      </div>
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-between items-baseline mb-8">
              <span className="text-sm uppercase tracking-widest-plus">Total</span>
              <span className="font-serif-display text-3xl" data-testid="cart-total">₹{total.toFixed(2)}</span>
            </div>
            <Link
              to="/checkout"
              className="w-full bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3.5 rounded-full text-sm uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors flex items-center justify-center gap-3"
              data-testid="cart-checkout-btn"
            >
              Checkout
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link
              to="/shop"
              className="mt-3 block text-center text-xs uppercase tracking-widest-plus text-[#9BA595] hover:text-[#D27D60] transition-colors"
              data-testid="cart-continue-shopping"
            >
              Continue shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
